import { useEffect, useState } from "react";
import { useRouterState } from "@tanstack/react-router";

const ACORN_URL =
  "https://fs.acornfinance.com/assets/portal/images/sharing/facebook/facebook-exterior-remodel-1.html?d=WZ1XX";

// Routes where Acorn financing doesn't apply (welding / fabrication work, not outdoor living).
const HIDDEN_ROUTES = ["/dog-boxes"];

export function FinancingCTA() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const hidden = HIDDEN_ROUTES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (hidden) return null;

  const trackOpen = () => {
    if (typeof window === "undefined") return;
    const gtag = (window as any).gtag;
    if (typeof gtag === "function") {
      gtag("event", "financing_cta_open", {
        event_category: "financing",
        event_label: "acorn_finance",
      });
    }
    void import("@/lib/analytics").then(({ track }) => {
      track("cta_click", { target: "acorn_finance", metadata: { placement: "global_floating", cta: "financing_open" } });
    });
  };

  const handleOpen = () => {
    trackOpen();
    setOpen(true);
  };

  const handleContinue = () => {
    setOpen(false);

  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        aria-label="Open financing options"
        data-cta-placement="global_floating"
        className="fixed bottom-5 left-5 z-[85] inline-flex items-center gap-2 border-4 border-navy-deep bg-white px-4 py-3 font-display text-xs uppercase tracking-wider text-navy-deep shadow-bold transition hover:bg-navy-deep hover:text-white md:text-sm print:hidden mb-16 md:mb-0"
      >
        <span aria-hidden="true">💳</span>
        <span>Financing Available</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Financing options"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="relative w-full max-w-md overflow-hidden border-4 border-navy-deep bg-white shadow-bold">
            <div className="flex items-start justify-between gap-4 border-b-2 border-navy-deep bg-navy-deep px-4 py-3 text-white">
              <h2 className="font-display text-lg uppercase tracking-wider">Affordable Financing</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close financing popup"
                className="shrink-0 border-2 border-white/70 px-3 py-1 font-display text-sm uppercase tracking-wider text-white transition hover:bg-white hover:text-navy-deep"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4 px-5 py-5 text-sm text-navy-deep">
              <p>
                We help you find affordable payment options in less than a minute. Compare personalized
                offers from lenders in our network, with <strong>no impact to your credit score</strong>.
              </p>
              <ul className="list-disc space-y-1 pl-5 text-navy-deep/80">
                <li>Pre-qualify in about 60 seconds</li>
                <li>Compare offers from multiple lenders</li>
                <li>Soft credit check — no score impact</li>
              </ul>
              <a
                href={ACORN_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleContinue}
                aria-label="Continue to Acorn Finance (opens in a new tab)"
                className="block w-full border-4 border-navy-deep bg-navy-deep px-4 py-3 text-center font-display text-sm uppercase tracking-wider text-white shadow-bold transition hover:bg-white hover:text-navy-deep"
              >
                Continue to Acorn Finance →
              </a>
              <p className="text-center text-[11px] text-navy-deep/60">
                Opens in a new tab. Financing provided by Acorn Finance and its lender network.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default FinancingCTA;
